import { useCallback } from "react";

import { Toast } from "@covertree/utils";

import {
  discardEndorsements,
  issueEndorsement,
  IssueEndorsementStatus,
  selectEndorsementId,
  selectEndorsementIssueBlockReason,
  selectHasUnsavedChanges,
  setEndorsementIssueBlockReason,
  useEndorsementSelector,
} from "@redux/slices/endorsement";
import { useAppDispatch } from "@redux/hooks";
import { useEndorsementOutletContext } from "@layouts/EndorsementLayout/EndorsementLayout";

export function useConflictingEndorsements() {
  const dispatch = useAppDispatch();
  const { policy, close, saveAsDraftAndClose } = useEndorsementOutletContext();
  const endorsementId = useEndorsementSelector(selectEndorsementId);
  const hasUnsavedChanges = useEndorsementSelector(selectHasUnsavedChanges);
  const blockReason = useEndorsementSelector(selectEndorsementIssueBlockReason);

  const isOpen =
    blockReason === IssueEndorsementStatus.ConflictingEndorsementError;

  const closeModal = useCallback(
    () => dispatch(setEndorsementIssueBlockReason(undefined)),
    [dispatch]
  );

  const saveAndExit = useCallback(async () => {
    if (hasUnsavedChanges) {
      await saveAsDraftAndClose();

      return;
    }
    close();
  }, [hasUnsavedChanges, saveAsDraftAndClose, close]);

  const discardConflicting = useCallback(async () => {
    const discarded = await dispatch(
      discardEndorsements({
        policyLocator: policy.policyLocator,
        endorsementLocators: endorsementId ? [endorsementId] : undefined,
        discardAllExceptSelected: true,
        silent: true,
      })
    );

    if (discardEndorsements.fulfilled.match(discarded)) {
      Toast.showSuccess(
        "Conflicting endorsements were successfully discarded!"
      );

      closeModal();

      const issued = await dispatch(issueEndorsement());

      if (issueEndorsement.fulfilled.match(issued)) {
        close();
      }
    }
  }, [dispatch, policy.policyLocator, endorsementId, close]);

  return { isOpen, closeModal, saveAndExit, discardConflicting };
}
